/**
 * Frontend API client — single source of truth for all backend calls.
 */

const API_BASE = process.env.REACT_APP_API_URL || '/api';

async function request(path, options = {}) {
  const url = `${API_BASE}${path}`;

  const headers = { 'Content-Type': 'application/json', ...options.headers };

  const token = localStorage.getItem('ta_token');
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  let res;
  try {
    res = await fetch(url, { ...options, headers });
  } catch (cause) {
    // fetch only rejects on network/CORS/CSP failures, never on HTTP error statuses
    const err = new Error('Network request failed');
    err.status = 0;
    err.code = 'NETWORK_ERROR';
    err.cause = cause;
    throw err;
  }
  const body = await res.json().catch(() => null);

  if (!res.ok) {
    const message = body?.error?.message || res.statusText || 'Request failed';
    const err = new Error(message);
    err.status = res.status;
    err.code = body?.error?.code;
    err.details = body?.error?.details;
    err.body = body;
    throw err;
  }

  return body;
}

// ─── Public ────────────────────────────────────────────────────────

export async function submitContactForm({ name, email, company, phone, message, turnstileToken }) {
  return request('/contact', {
    method: 'POST',
    body: JSON.stringify({ name, email, company, phone, message, turnstileToken }),
  });
}

export async function submitRfq({ name, email, phone, company, americaMakesMember, message, configuration, estimatedPrice, priceBreakdown, turnstileToken }) {
  return request('/rfq', {
    method: 'POST',
    body: JSON.stringify({ name, email, phone, company, americaMakesMember, message, configuration, estimatedPrice, priceBreakdown, turnstileToken }),
  });
}

export async function getActiveClearBoxConfig() {
  return request('/clearbox-config');
}

// ─── Auth ──────────────────────────────────────────────────────────

export async function login(email, password) {
  const result = await request('/auth/login', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  });
  if (result?.data?.token) {
    localStorage.setItem('ta_token', result.data.token);
  }
  return result;
}

export function logout() {
  localStorage.removeItem('ta_token');
}

export async function register({ email, password, name }) {
  return request('/auth/register', {
    method: 'POST',
    body: JSON.stringify({ email, password, name }),
  });
}

// ─── Admin ─────────────────────────────────────────────────────────

export async function getContacts({ page = 1, limit = 20, read } = {}) {
  const params = new URLSearchParams({ page, limit });
  if (read !== undefined) params.set('read', read);
  return request(`/admin/contacts?${params}`);
}

export async function markContactRead(id) {
  return request(`/admin/contacts/${id}/read`, { method: 'PATCH' });
}

export async function getRfqs({ page = 1, limit = 20, status } = {}) {
  const params = new URLSearchParams({ page, limit });
  if (status !== undefined) params.set('status', status);
  return request(`/admin/rfqs?${params}`);
}

export async function updateRfqStatus(id, status) {
  return request(`/admin/rfqs/${id}/status`, {
    method: 'PATCH',
    body: JSON.stringify({ status }),
  });
}

export async function deleteContact(id) {
  return request(`/admin/contacts/${id}`, { method: 'DELETE' });
}

export async function deleteRfq(id) {
  return request(`/admin/rfqs/${id}`, { method: 'DELETE' });
}

export async function deleteContactsBulk(ids) {
  return request('/admin/contacts/bulk-delete', {
    method: 'POST',
    body: JSON.stringify({ ids }),
  });
}

export async function deleteRfqsBulk(ids) {
  return request('/admin/rfqs/bulk-delete', {
    method: 'POST',
    body: JSON.stringify({ ids }),
  });
}

export async function updateClearBoxConfig(id, data) {
  return request(`/admin/clearbox-config/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function getAdminStats() {
  // Fetch counts in parallel
  const [contacts, demos] = await Promise.all([
    getContacts({ limit: 1 }),
    getRfqs({ limit: 1 }),
  ]);
  return {
    totalContacts: contacts.total || 0,
    totalRfqs: demos.total || 0,
  };
}

export async function getUnreadCounts() {
  // Fetch unread counts in parallel
  const [contacts, demos] = await Promise.all([
    getContacts({ limit: 1, read: false }),
    getRfqs({ limit: 1, status: 'UNREAD' }),
  ]);
  return {
    unreadContacts: contacts.total || 0,
    unreadRfqs: demos.total || 0,
  };
}

// ─── Password Reset & Registration ─────────────────────────────────

export async function forgotPassword(email) {
  return request('/auth/forgot-password', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
}

export async function resetPassword(token, password, confirmPassword) {
  return request('/auth/reset-password', {
    method: 'POST',
    body: JSON.stringify({ token, password, confirmPassword }),
  });
}

export async function inviteAdmin(email, name, phone) {
  return request('/auth/invite', {
    method: 'POST',
    body: JSON.stringify({ email, name, phone }),
  });
}

export async function completeRegistration(token, password, confirmPassword) {
  const result = await request('/auth/register', {
    method: 'POST',
    body: JSON.stringify({ token, password, confirmPassword }),
  });
  // Auto-login after registration
  if (result?.data?.token) {
    localStorage.setItem('ta_token', result.data.token);
  }
  return result;
}

// ─── Admin User Management ─────────────────────────────────────────

export async function getAdmins() {
  return request('/admin/users');
}

export async function revokeAdmin(id) {
  return request(`/admin/users/${id}`, { method: 'DELETE' });
}

// ─── Profile Management ────────────────────────────────────────────

export async function updateProfile({ name, email, phone }) {
  return request('/admin/profile', {
    method: 'PUT',
    body: JSON.stringify({ name, email, phone }),
  });
}

export async function changePassword({ currentPassword, newPassword, confirmPassword }) {
  return request('/admin/password', {
    method: 'PUT',
    body: JSON.stringify({ currentPassword, newPassword, confirmPassword }),
  });
}

// ─── Quotes ─────────────────────────────────────────────────────────

export async function getQuotes({ page = 1, limit = 20, status, search } = {}) {
  const params = new URLSearchParams({ page, limit });
  if (status !== undefined) params.set('status', status);
  if (search) params.set('search', search);
  return request(`/admin/quotes?${params}`);
}

export async function getQuote(id) {
  return request(`/admin/quotes/${id}`);
}

export async function getQuotePrefill(rfqId) {
  return request(`/admin/quotes/prefill/${rfqId}`);
}

export async function createQuote(data) {
  return request('/admin/quotes', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateQuote(id, data) {
  return request(`/admin/quotes/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteQuote(id) {
  return request(`/admin/quotes/${id}`, { method: 'DELETE' });
}

export async function deleteQuotesBulk(ids) {
  return request('/admin/quotes/bulk-delete', {
    method: 'POST',
    body: JSON.stringify({ ids }),
  });
}

// Downloads a generated quote document; returns the blob + suggested filename for the caller to save.
export async function exportQuote(id, format) {
  const token = localStorage.getItem('ta_token');
  const res = await fetch(`${API_BASE}/admin/quotes/${id}/export?format=${format}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });

  if (!res.ok) {
    const body = await res.json().catch(() => null);
    const err = new Error(body?.error?.message || 'Export failed');
    err.status = res.status;
    throw err;
  }

  const disposition = res.headers.get('Content-Disposition') || '';
  const match = disposition.match(/filename="?([^"]+)"?/);
  const filename = match ? match[1] : `quote.${format}`;
  const blob = await res.blob();
  return { blob, filename };
}
